/**
 * useEnterpriseListController
 *
 * Enterprise list hook: debounced search, filters, sort, pagination with:
 * - Race-condition prevention (AbortController + requestId)
 * - Batched URL sync (primary SoT when urlSync enabled)
 * - Optional sessionStorage mirror (compatibility; URL wins when both present)
 * - Optional in-memory list query cache (stale-while-revalidate)
 *
 * URL vs sessionStorage precedence (when urlSyncConfig.enabled):
 *   1. URL query params
 *   2. sessionStorage (only if URL has no value for that field / cold start without params)
 *   3. schema defaults
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, MIN_PAGE_SIZE } from '../../constants/pagination';
import {
  buildListCacheKey,
  parseClampedInt,
  parsePositiveInt,
  parseSortDir,
  parseEnum,
  serializeListSearchParams,
  normalizeSearchString,
} from '../utils/listUrlState.js';
import {
  getListCache,
  setListCache,
  getListStaleMs,
  invalidateListDomain,
} from './listQueryCache.js';
import { rememberCurrentListLocation } from '../../constants/nav.js';

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function safeJsonParse(raw, fallback) {
  try {
    return raw != null ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function readStorage(key, fallback) {
  if (!key) return fallback;
  try {
    const stored = sessionStorage.getItem(key);
    if (stored == null) return fallback;
    return safeJsonParse(stored, fallback);
  } catch {
    return fallback;
  }
}

function writeStorage(key, value, isDefault) {
  if (!key) return;
  try {
    if (isDefault || value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

/**
 * @param {Object} config
 * @param {Function} config.fetchFn
 * @param {Object} [config.searchConfig]
 * @param {Object} [config.filterConfig]
 * @param {Object} [config.sortConfig]
 * @param {Object} [config.paginationConfig]
 * @param {Object} [config.urlSyncConfig] { enabled, pathname }
 * @param {Object} [config.cacheConfig] { domain: string, staleTime?: number, enabled?: boolean }
 * @param {boolean} [config.rememberListReturn] store list return URL for navigateToList
 */
export default function useEnterpriseListController(config = {}) {
  const {
    fetchFn,
    searchConfig = {},
    filterConfig = {},
    sortConfig = {},
    paginationConfig = {},
    urlSyncConfig = {},
    cacheConfig = null,
    rememberListReturn = false,
  } = config;

  const location = useLocation();
  const navigate = useNavigate();
  const pathname = urlSyncConfig.pathname || location.pathname;
  const urlSyncEnabled = urlSyncConfig.enabled === true;

  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const urlWriteLockRef = useRef(false);

  const searchDebounceMs = searchConfig.debounceMs ?? 300;
  const searchStorageKey = searchConfig.storageKey;
  const searchUrlSync = searchConfig.urlSync !== false && urlSyncEnabled;

  const filterStorageKey = filterConfig.storageKey;
  const filterUrlSync = filterConfig.urlSync !== false && urlSyncEnabled;
  const defaultFilters = filterConfig.filters || {};

  const sortDefaultKey = sortConfig.defaultKey || 'id';
  const sortDefaultDir = sortConfig.defaultDir || 'desc';
  const sortAllowedKeys = sortConfig.allowedKeys || [];
  const sortStorageKey = sortConfig.storageKey;
  const sortUrlSync = sortConfig.urlSync !== false && urlSyncEnabled;

  const defaultPageSize = paginationConfig.defaultPageSize || DEFAULT_PAGE_SIZE;
  const maxPageSizeAllowed = paginationConfig.maxPageSize ?? MAX_PAGE_SIZE;
  const paginationUrlSync = paginationConfig.urlSync !== false && urlSyncEnabled;

  const cacheDomain = cacheConfig?.domain || null;
  const cacheEnabled = !!(cacheDomain && cacheConfig?.enabled !== false);
  const cacheStaleTime = cacheConfig?.staleTime ?? (cacheDomain ? getListStaleMs(cacheDomain) : 0);

  // ——— Initial state: URL wins when urlSync enabled ———
  const initialUrlParams = useMemo(() => {
    if (!urlSyncEnabled) return new URLSearchParams();
    return new URLSearchParams(location.search);
    // only for mount init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [search, setSearch] = useState(() => {
    if (searchUrlSync) {
      const fromUrl = initialUrlParams.get('q');
      if (fromUrl != null) return fromUrl;
    }
    if (searchStorageKey) return String(readStorage(searchStorageKey, '') || '');
    return '';
  });
  const debouncedSearch = useDebounce(search, searchDebounceMs);

  const [filters, setFilters] = useState(() => {
    const stored = filterStorageKey ? readStorage(filterStorageKey, defaultFilters) : defaultFilters;
    const base = { ...defaultFilters, ...(stored && typeof stored === 'object' ? stored : {}) };
    if (filterUrlSync) {
      const next = { ...base };
      for (const key of Object.keys(defaultFilters)) {
        const fromUrl = initialUrlParams.get(`filter_${key}`);
        if (fromUrl != null) next[key] = fromUrl;
      }
      return next;
    }
    return base;
  });

  const [sort, setSort] = useState(() => {
    const stored = sortStorageKey
      ? readStorage(sortStorageKey, { key: sortDefaultKey, dir: sortDefaultDir })
      : { key: sortDefaultKey, dir: sortDefaultDir };
    let key = stored?.key || sortDefaultKey;
    let dir = parseSortDir(stored?.dir, sortDefaultDir);
    if (sortUrlSync) {
      if (initialUrlParams.has('sort')) {
        key = parseEnum(initialUrlParams.get('sort'), sortAllowedKeys.length ? sortAllowedKeys : null, sortDefaultKey);
      }
      if (initialUrlParams.has('dir')) {
        dir = parseSortDir(initialUrlParams.get('dir'), sortDefaultDir);
      }
    }
    if (sortAllowedKeys.length > 0 && !sortAllowedKeys.includes(key)) {
      key = sortDefaultKey;
      dir = sortDefaultDir;
    }
    return { key, dir };
  });

  const [pagination, setPagination] = useState(() => {
    let page = 1;
    let pageSize = defaultPageSize;
    if (paginationUrlSync) {
      page = parsePositiveInt(initialUrlParams.get('page'), 1);
      pageSize = parseClampedInt(initialUrlParams.get('pageSize'), {
        min: MIN_PAGE_SIZE,
        max: maxPageSizeAllowed,
        fallback: defaultPageSize,
      });
    }
    return { page, pageSize };
  });

  const queryKey = useMemo(
    () =>
      JSON.stringify({
        search: debouncedSearch,
        filters,
        sort,
        page: pagination.page,
        pageSize: pagination.pageSize,
      }),
    [debouncedSearch, filters, sort, pagination],
  );

  const cacheKey = useMemo(() => {
    if (!cacheEnabled) return null;
    return buildListCacheKey(cacheDomain, {
      q: debouncedSearch,
      filters,
      sort,
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
  }, [cacheEnabled, cacheDomain, debouncedSearch, filters, sort, pagination]);

  const initialCache = useMemo(() => {
    if (!cacheKey) return null;
    return getListCache(cacheKey, { staleTime: cacheStaleTime });
  }, []); // mount snapshot only — live reads happen in fetch effect

  const [items, setItems] = useState(() => initialCache?.entry?.items || []);
  const [total, setTotal] = useState(() => initialCache?.entry?.total || 0);
  const [loading, setLoading] = useState(() => !(initialCache?.fresh));
  const [revalidating, setRevalidating] = useState(false);
  const [error, setError] = useState(null);

  const filtersRef = useRef(filters);
  const sortRef = useRef(sort);
  const paginationRef = useRef(pagination);
  const debouncedSearchRef = useRef(debouncedSearch);
  const queryKeyRef = useRef(queryKey);
  const itemsRef = useRef(items);

  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { sortRef.current = sort; }, [sort]);
  useEffect(() => { paginationRef.current = pagination; }, [pagination]);
  useEffect(() => { debouncedSearchRef.current = debouncedSearch; }, [debouncedSearch]);
  useEffect(() => { queryKeyRef.current = queryKey; }, [queryKey]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // ——— Batched URL sync (single navigate per logical state) ———
  useEffect(() => {
    if (!urlSyncEnabled) return;
    if (!(searchUrlSync || filterUrlSync || sortUrlSync || paginationUrlSync)) return;

    const params = serializeListSearchParams(
      {
        q: searchUrlSync ? debouncedSearch : undefined,
        filters: filterUrlSync ? filters : undefined,
        sort: sortUrlSync ? sort : undefined,
        pagination: paginationUrlSync ? pagination : undefined,
      },
      {
        sortKey: sortDefaultKey,
        sortDir: sortDefaultDir,
        page: 1,
        pageSize: defaultPageSize,
        filters: defaultFilters,
      },
    );

    // If search URL sync is off, preserve existing q from location
    if (!searchUrlSync) {
      const cur = new URLSearchParams(location.search);
      if (cur.get('q')) params.set('q', cur.get('q'));
    }

    const nextSearch = normalizeSearchString(params.toString());
    const curSearch = normalizeSearchString(location.search);
    if (nextSearch === curSearch) return;
    // Only write when we own this pathname
    if (location.pathname !== pathname && urlSyncConfig.pathname && location.pathname !== urlSyncConfig.pathname) {
      return;
    }

    urlWriteLockRef.current = true;
    const qs = nextSearch ? `?${nextSearch}` : '';
    navigate(`${pathname}${qs}`, { replace: true });
    // release lock after paint
    queueMicrotask(() => {
      urlWriteLockRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlSyncEnabled,
    searchUrlSync,
    filterUrlSync,
    sortUrlSync,
    paginationUrlSync,
    debouncedSearch,
    filters,
    sort,
    pagination,
    pathname,
    sortDefaultKey,
    sortDefaultDir,
    defaultPageSize,
  ]);

  // sessionStorage mirrors — only when URL sync is OFF for that field (Wave 5: URL is SoT)
  useEffect(() => {
    if (searchStorageKey && !searchUrlSync) writeStorage(searchStorageKey, debouncedSearch, !debouncedSearch);
  }, [debouncedSearch, searchStorageKey, searchUrlSync]);

  useEffect(() => {
    if (filterStorageKey && !filterUrlSync) {
      const isDefault = JSON.stringify(filters) === JSON.stringify(defaultFilters);
      writeStorage(filterStorageKey, filters, isDefault);
    }
  }, [filters, filterStorageKey, defaultFilters, filterUrlSync]);

  useEffect(() => {
    if (sortStorageKey && !sortUrlSync) {
      const isDefault = sort.key === sortDefaultKey && sort.dir === sortDefaultDir;
      writeStorage(sortStorageKey, sort, isDefault);
    }
  }, [sort, sortStorageKey, sortDefaultKey, sortDefaultDir, sortUrlSync]);

  // Remember list return URL for explicit list buttons
  useEffect(() => {
    if (!rememberListReturn || !urlSyncEnabled) return;
    rememberCurrentListLocation(pathname, {
      pathname,
      search: location.search,
    });
  }, [rememberListReturn, urlSyncEnabled, pathname, location.search]);

  // Reset page when list-defining query changes
  const listQueryKeyWithoutPage = useMemo(
    () => JSON.stringify({ search: debouncedSearch, filters, sort }),
    [debouncedSearch, filters, sort],
  );
  const prevListQueryRef = useRef(listQueryKeyWithoutPage);
  useEffect(() => {
    if (prevListQueryRef.current === listQueryKeyWithoutPage) return;
    prevListQueryRef.current = listQueryKeyWithoutPage;
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [listQueryKeyWithoutPage]);

  const fetchData = useCallback(async ({ bypassCache = false } = {}) => {
    if (!fetchFn) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const ac = new AbortController();
    abortControllerRef.current = ac;
    const currentRequestId = ++requestIdRef.current;
    const activeQueryKey = queryKeyRef.current;

    const currentFilters = filtersRef.current;
    const currentSort = sortRef.current;
    const currentPagination = paginationRef.current;
    const currentSearch = debouncedSearchRef.current;

    const activeCacheKey = cacheEnabled
      ? buildListCacheKey(cacheDomain, {
          q: currentSearch,
          filters: currentFilters,
          sort: currentSort,
          page: currentPagination.page,
          pageSize: currentPagination.pageSize,
        })
      : null;

    if (activeCacheKey && !bypassCache) {
      const hit = getListCache(activeCacheKey, { staleTime: cacheStaleTime });
      if (hit.entry) {
        setItems(hit.entry.items);
        setTotal(hit.entry.total);
        setError(null);
        if (hit.fresh) {
          setLoading(false);
          setRevalidating(false);
          return;
        }
        // stale-while-revalidate
        setLoading(false);
        setRevalidating(true);
      } else {
        // New query context without cache: avoid acting on previous filter rows
        setItems([]);
        setTotal(0);
        setLoading(true);
        setRevalidating(false);
      }
    } else {
      setLoading(true);
      setRevalidating(false);
    }

    setError(null);

    try {
      const filterEntries = Object.entries(currentFilters)
        .map(([key, value]) => {
          if (value === '__all__' || value === null || value === '') return [key, undefined];
          return [key, value];
        })
        .filter(([, value]) => value !== undefined);

      const queryParams = {
        q: currentSearch || undefined,
        ...Object.fromEntries(filterEntries),
        sort: currentSort.key,
        dir: currentSort.dir,
        page: currentPagination.page,
        pageSize: currentPagination.pageSize,
      };

      const result = await fetchFn(queryParams, ac.signal);

      if (ac.signal.aborted || currentRequestId !== requestIdRef.current) return;
      if (queryKeyRef.current !== activeQueryKey) return;

      let normalizedItems = [];
      let normalizedTotal = 0;
      let normalizedPage = currentPagination.page;
      let normalizedPageSize = currentPagination.pageSize;

      if (Array.isArray(result)) {
        normalizedItems = result;
        normalizedTotal = result.length;
      } else if (result && typeof result === 'object') {
        normalizedItems = Array.isArray(result.items) ? result.items : [];
        normalizedTotal = result.total ?? result.count ?? normalizedItems.length;
        normalizedPage = result.page ?? currentPagination.page;
        normalizedPageSize = result.pageSize ?? result.page_size ?? currentPagination.pageSize;
      }

      setItems(normalizedItems);
      setTotal(normalizedTotal);
      if (normalizedPage !== currentPagination.page || normalizedPageSize !== currentPagination.pageSize) {
        setPagination({ page: normalizedPage, pageSize: normalizedPageSize });
      }
      setError(null);

      if (activeCacheKey) {
        setListCache(activeCacheKey, {
          items: normalizedItems,
          total: normalizedTotal,
          page: normalizedPage,
          pageSize: normalizedPageSize,
        });
      }
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || ac.signal.aborted) return;
      if (currentRequestId !== requestIdRef.current) return;
      console.error('Fetch error:', err);
      setError(err?.response?.data?.error || err?.message || 'Fehler beim Laden der Daten');
      // Keep previous rows when we already showed something; only clear on cold failure
      if (!itemsRef.current?.length) {
        setItems([]);
        setTotal(0);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        setRevalidating(false);
      }
    }
  }, [fetchFn, cacheEnabled, cacheDomain, cacheStaleTime]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const refetch = useCallback(() => {
    if (cacheDomain) invalidateListDomain(cacheDomain);
    fetchData({ bypassCache: true });
  }, [fetchData, cacheDomain]);

  const setSearchValue = useCallback((value) => {
    setSearch(String(value || ''));
  }, []);

  const setFilterValue = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSortKey = useCallback((key) => {
    setSort((prev) => {
      if (sortAllowedKeys.length > 0 && !sortAllowedKeys.includes(key)) return prev;
      const defaultDir = sortConfig.defaultDirFor?.(key) || sortDefaultDir;
      return { key, dir: prev.key === key ? prev.dir : defaultDir };
    });
  }, [sortAllowedKeys, sortConfig.defaultDirFor, sortDefaultDir]);

  const setSortDir = useCallback((dir) => {
    setSort((prev) => ({ ...prev, dir: dir === 'asc' ? 'asc' : 'desc' }));
  }, []);

  const toggleSortDir = useCallback(() => {
    setSort((prev) => ({ ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));
  }, []);

  const setPage = useCallback((page) => {
    setPagination((prev) => ({ ...prev, page: Math.max(1, parseInt(String(page), 10) || 1) }));
  }, []);

  const setPageSize = useCallback((pageSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: Math.max(MIN_PAGE_SIZE, Math.min(maxPageSizeAllowed, parseInt(String(pageSize), 10) || defaultPageSize)),
      page: 1,
    }));
  }, [defaultPageSize, maxPageSizeAllowed]);

  const pages = useMemo(() => Math.ceil(total / pagination.pageSize) || 1, [total, pagination.pageSize]);
  const hasNextPage = useMemo(() => pagination.page < pages, [pagination.page, pages]);
  const hasPrevPage = useMemo(() => pagination.page > 1, [pagination.page]);

  return {
    items,
    total,
    loading,
    revalidating,
    error,
    search,
    setSearch: setSearchValue,
    debouncedSearch,
    filters,
    setFilters,
    setFilter: setFilterValue,
    sort,
    setSort,
    setSortKey,
    setSortDir,
    toggleSortDir,
    pagination,
    setPagination,
    setPage,
    setPageSize,
    pages,
    hasNextPage,
    hasPrevPage,
    refetch,
    queryKey,
    cacheKey,
    cacheDomain,
  };
}
